/**
 * Page Canvas
 * 
 * The main canvas area showing:
 * - Paper background (white page)
 * - Captured map image (centered or fitted)
 * - Draggable and resizable annotation elements
 */

import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import html2canvas from 'html2canvas';
import type { PageAnnotation, AnnotationType } from '../../types/annotations';
import { AnnotationRenderer } from './AnnotationRenderer';

export interface PageCanvasHandle {
  captureCanvas: () => Promise<string | null>;
}

interface PageCanvasProps {
  width: number;
  height: number;
  zoom: number;
  panOffset: { x: number; y: number };
  onPanChange: (offset: { x: number; y: number }) => void;
  capturedCanvas: string | null;
  annotations: PageAnnotation[];
  selectedId: string | null;
  activeTool: AnnotationType | null;
  onAnnotationAdd: (type: AnnotationType, position: { x: number; y: number }) => void;
  onAnnotationSelect: (id: string | null) => void;
  onAnnotationMove: (id: string, position: { x: number; y: number }) => void;
  onAnnotationResize: (id: string, width: number, height: number) => void;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

// Border padding as percentage of page dimensions (matches backend _MAP_MARGIN / _DOCUMENT_PADDING)
export const BORDER_PADDING_PERCENT = 0.025; // 2.5% of page dimension
const EXPORT_BORDER_COLOR = '#4B5563'; // Lighter gray-black border
const BRANDING_TEXT = 'www.trafficdesigner.ai';

export const PageCanvas = forwardRef<PageCanvasHandle, PageCanvasProps>(({
  width,
  height,
  zoom,
  panOffset,
  onPanChange,
  capturedCanvas,
  annotations,
  selectedId,
  activeTool,
  onAnnotationAdd,
  onAnnotationSelect,
  onAnnotationMove,
  onAnnotationResize,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState<{ id: string; handle: ResizeHandle; startX: number; startY: number; startWidth: number; startHeight: number; startPosX: number; startPosY: number } | null>(null);

  // Expose capture function to parent via ref
  useImperativeHandle(ref, () => ({
    captureCanvas: async (): Promise<string | null> => {
      if (!pageRef.current) return null;
      
      try {
        console.log('Capturing annotation canvas...');
        
        // High-res scale factor to match the map capture quality (4x)
        const exportScale = 4;

        // Get the page element and capture at high resolution
        const canvas = await html2canvas(pageRef.current, {
          scale: exportScale / zoom, // Counter CSS zoom AND upscale for sharp output
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: width * zoom,
          height: height * zoom,
          logging: false,
          // Clean up UI elements during capture
          onclone: (doc) => {
            const clonedPage = doc.querySelector('[data-page]');
            if (clonedPage) {
              // Remove any selection outlines
              const selectedElements = clonedPage.querySelectorAll('[style*="outline"]');
              selectedElements.forEach(el => {
                (el as HTMLElement).style.outline = 'none';
              });
              // Hide disabled title box placeholders
              const disabledTitleBoxes = clonedPage.querySelectorAll('[data-disabled-titlebox]');
              disabledTitleBoxes.forEach(el => {
                (el as HTMLElement).style.display = 'none';
              });
            }
          }
        });

        // Return as high-res data URL
        const dataUrl = canvas.toDataURL('image/png');
        console.log(`Annotation canvas captured: ${canvas.width}x${canvas.height}px`);
        return dataUrl;
      } catch (error) {
        console.error('Error capturing annotation canvas:', error);
        return null;
      }
    }
  }), [zoom, width, height]);

  // Handle click on canvas (for placing new annotations)
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (!activeTool || !containerRef.current) return;
    
    const pageElement = containerRef.current.querySelector('[data-page]');
    if (!pageElement) return;
    
    const pageRect = (pageElement as HTMLElement).getBoundingClientRect();
    const x = (e.clientX - pageRect.left) / zoom;
    const y = (e.clientY - pageRect.top) / zoom;
    
    if (x >= 0 && x <= width && y >= 0 && y <= height) {
      onAnnotationAdd(activeTool, { x, y });
    }
  }, [activeTool, zoom, width, height, onAnnotationAdd]);

  // Handle click on empty area (deselect)
  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !activeTool) {
      onAnnotationSelect(null);
    }
  }, [activeTool, onAnnotationSelect]);

  // Panning with middle mouse or alt+drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    }
  }, [panOffset]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      onPanChange({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  }, [isPanning, panStart, onPanChange]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Annotation drag handlers
  const handleAnnotationDragStart = useCallback((id: string, e: React.MouseEvent) => {
    const annotation = annotations.find(a => a.id === id);
    if (!annotation || annotation.locked) return;
    
    setDraggingId(id);
    onAnnotationSelect(id);
    
    const pageElement = containerRef.current?.querySelector('[data-page]');
    if (pageElement) {
      const pageRect = (pageElement as HTMLElement).getBoundingClientRect();
      const mouseX = (e.clientX - pageRect.left) / zoom;
      const mouseY = (e.clientY - pageRect.top) / zoom;
      setDragOffset({
        x: mouseX - annotation.position.x,
        y: mouseY - annotation.position.y,
      });
    }
    
    e.stopPropagation();
  }, [annotations, zoom, onAnnotationSelect]);

  // Resize handlers
  const handleResizeStart = useCallback((id: string, handle: ResizeHandle, e: React.MouseEvent) => {
    const annotation = annotations.find(a => a.id === id);
    if (!annotation || annotation.locked) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    setResizing({
      id,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: annotation.width,
      startHeight: annotation.height,
      startPosX: annotation.position.x,
      startPosY: annotation.position.y,
    });
  }, [annotations]);

  // Global mouse move/up for dragging and resizing
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Handle dragging
      if (draggingId && containerRef.current) {
        const pageElement = containerRef.current.querySelector('[data-page]');
        if (!pageElement) return;
        
        const pageRect = (pageElement as HTMLElement).getBoundingClientRect();
        const x = (e.clientX - pageRect.left) / zoom - dragOffset.x;
        const y = (e.clientY - pageRect.top) / zoom - dragOffset.y;
        
        const annotation = annotations.find(a => a.id === draggingId);
        if (annotation) {
          const clampedX = Math.max(0, Math.min(x, width - annotation.width));
          const clampedY = Math.max(0, Math.min(y, height - annotation.height));
          onAnnotationMove(draggingId, { x: clampedX, y: clampedY });
        }
      }
      
      // Handle resizing
      if (resizing) {
        const deltaX = (e.clientX - resizing.startX) / zoom;
        const deltaY = (e.clientY - resizing.startY) / zoom;
        
        let newWidth = resizing.startWidth;
        let newHeight = resizing.startHeight;
        let newX = resizing.startPosX;
        let newY = resizing.startPosY;
        
        // Calculate new dimensions based on handle
        switch (resizing.handle) {
          case 'e':
            newWidth = Math.max(50, resizing.startWidth + deltaX);
            break;
          case 'w':
            newWidth = Math.max(50, resizing.startWidth - deltaX);
            newX = resizing.startPosX + (resizing.startWidth - newWidth);
            break;
          case 's':
            newHeight = Math.max(30, resizing.startHeight + deltaY);
            break;
          case 'n':
            newHeight = Math.max(30, resizing.startHeight - deltaY);
            newY = resizing.startPosY + (resizing.startHeight - newHeight);
            break;
          case 'se':
            newWidth = Math.max(50, resizing.startWidth + deltaX);
            newHeight = Math.max(30, resizing.startHeight + deltaY);
            break;
          case 'sw':
            newWidth = Math.max(50, resizing.startWidth - deltaX);
            newHeight = Math.max(30, resizing.startHeight + deltaY);
            newX = resizing.startPosX + (resizing.startWidth - newWidth);
            break;
          case 'ne':
            newWidth = Math.max(50, resizing.startWidth + deltaX);
            newHeight = Math.max(30, resizing.startHeight - deltaY);
            newY = resizing.startPosY + (resizing.startHeight - newHeight);
            break;
          case 'nw':
            newWidth = Math.max(50, resizing.startWidth - deltaX);
            newHeight = Math.max(30, resizing.startHeight - deltaY);
            newX = resizing.startPosX + (resizing.startWidth - newWidth);
            newY = resizing.startPosY + (resizing.startHeight - newHeight);
            break;
        }
        
        // Clamp to page bounds
        newX = Math.max(0, Math.min(newX, width - newWidth));
        newY = Math.max(0, Math.min(newY, height - newHeight));
        
        onAnnotationMove(resizing.id, { x: newX, y: newY });
        onAnnotationResize(resizing.id, newWidth, newHeight);
      }
    };

    const handleGlobalMouseUp = () => {
      setDraggingId(null);
      setResizing(null);
      setIsPanning(false);
    };

    if (draggingId || resizing || isPanning) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggingId, resizing, isPanning, dragOffset, zoom, width, height, annotations, onAnnotationMove, onAnnotationResize]);

  // Resize handle component
  const ResizeHandles: React.FC<{ id: string }> = ({ id }) => {
    const handles: { position: ResizeHandle; cursor: string; style: React.CSSProperties }[] = [
      { position: 'nw', cursor: 'nw-resize', style: { top: -4, left: -4 } },
      { position: 'n', cursor: 'n-resize', style: { top: -4, left: '50%', transform: 'translateX(-50%)' } },
      { position: 'ne', cursor: 'ne-resize', style: { top: -4, right: -4 } },
      { position: 'e', cursor: 'e-resize', style: { top: '50%', right: -4, transform: 'translateY(-50%)' } },
      { position: 'se', cursor: 'se-resize', style: { bottom: -4, right: -4 } },
      { position: 's', cursor: 's-resize', style: { bottom: -4, left: '50%', transform: 'translateX(-50%)' } },
      { position: 'sw', cursor: 'sw-resize', style: { bottom: -4, left: -4 } },
      { position: 'w', cursor: 'w-resize', style: { top: '50%', left: -4, transform: 'translateY(-50%)' } },
    ];

    return (
      <>
        {handles.map(({ position, cursor, style }) => (
          <Box
            key={position}
            onMouseDown={(e) => handleResizeStart(id, position, e)}
            sx={{
              position: 'absolute',
              width: 8,
              height: 8,
              backgroundColor: '#3B82F6',
              border: '1px solid white',
              borderRadius: '2px',
              cursor,
              zIndex: 20,
              ...style,
            }}
          />
        ))}
      </>
    );
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleBackgroundClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: activeTool ? 'crosshair' : isPanning ? 'grabbing' : 'default',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {/* The Page */}
        <Box
          ref={pageRef}
          data-page
          onClick={handleCanvasClick}
          sx={{
            width: width * zoom,
            height: height * zoom,
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Captured Map Image */}
          {(() => {
            // Check if there's an active title box to determine map positioning
            const titleBox = annotations.find(a => a.type === 'title-box' && (a as any).useTitleBox !== false);
            const titleBoxHeight = titleBox ? titleBox.height : 0;
            // Border padding in pixels (scaled)
            const borderPad = BORDER_PADDING_PERCENT * Math.min(width, height) * zoom;
            // Top padding — small gap for branding text just above the border
            const topPad = borderPad * 1.8;
            // Map area is the space above the title box, inset by border padding
            // Title box is positioned inside the border, so map fills from topPad to title box top
            const mapAreaHeightScaled = titleBox
              ? height * zoom - borderPad - titleBoxHeight * zoom - topPad
              : height * zoom - topPad - borderPad;

            if (capturedCanvas) {
              return (
                <Box
                  component="img"
                  src={capturedCanvas}
                  alt="Map"
                  sx={{
                    position: 'absolute',
                    top: topPad,
                    left: borderPad,
                    width: `calc(100% - ${borderPad * 2}px)`,
                    height: mapAreaHeightScaled,
                    // Cover fills the area completely; minor cropping is preferable to gaps
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    pointerEvents: 'none',
                  }}
                />
              );
            }

            // Placeholder when no map
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: topPad,
                  left: borderPad,
                  width: `calc(100% - ${borderPad * 2}px)`,
                  height: mapAreaHeightScaled,
                  backgroundColor: '#F3F4F6',
                  border: '2px dashed #D1D5DB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                }}
              >
                Map preview will appear here
              </Box>
            );
          })()}

          {/* Annotations Layer */}
          {annotations.map((annotation) => {
            const isSelected = selectedId === annotation.id;
            // Title box has fixed position at bottom - not draggable/resizable
            const isTitleBox = annotation.type === 'title-box';
            const isDisabledTitleBox = isTitleBox && (annotation as any).useTitleBox === false;
            const isLocked = annotation.locked || isTitleBox;

            return (
              <Box
                key={annotation.id}
                {...(isDisabledTitleBox ? { 'data-disabled-titlebox': 'true' } : {})}
                sx={{
                  position: 'absolute',
                  left: annotation.position.x * zoom,
                  top: annotation.position.y * zoom,
                  width: annotation.width * zoom,
                  height: annotation.height * zoom,
                  cursor: isLocked ? 'default' : (draggingId === annotation.id ? 'grabbing' : 'grab'),
                  outline: isSelected ? '2px solid #3B82F6' : 'none',
                  outlineOffset: 2,
                  zIndex: isTitleBox ? 5 : (isSelected ? 10 : 1),
                }}
                onMouseDown={(e) => {
                  if (!resizing && !isLocked) {
                    handleAnnotationDragStart(annotation.id, e);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnnotationSelect(annotation.id);
                }}
              >
                <Box
                  sx={{
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    width: annotation.width,
                    height: annotation.height,
                  }}
                >
                  <AnnotationRenderer annotation={annotation} />
                </Box>
                
                {/* Resize handles - only show when selected, not for title box */}
                {isSelected && !isLocked && (
                  <ResizeHandles id={annotation.id} />
                )}
              </Box>
            );
          })}

          {/* Branding URL - sits just above the content border */}
          {(() => {
            const borderPad = BORDER_PADDING_PERCENT * Math.min(width, height) * zoom;
            const topPad = borderPad * 1.8;
            const fontSize = Math.max(6, width * zoom * 0.009);
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: topPad - fontSize * 1.4,
                  right: borderPad,
                  fontSize,
                  fontWeight: 400,
                  color: EXPORT_BORDER_COLOR,
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  pointerEvents: 'none',
                  zIndex: 20,
                }}
              >
                {BRANDING_TEXT}
              </Box>
            );
          })()}

          {/* Content Border - surrounds map + title box area */}
          {(() => {
            const borderPad = BORDER_PADDING_PERCENT * Math.min(width, height) * zoom;
            const topPad = borderPad * 1.8;
            const borderWidth = Math.max(2, Math.round(width * zoom * 0.002));
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: topPad,
                  left: borderPad,
                  right: borderPad,
                  bottom: borderPad,
                  border: `${borderWidth}px solid ${EXPORT_BORDER_COLOR}`,
                  pointerEvents: 'none',
                  zIndex: 15,
                }}
              />
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
});

export default PageCanvas;
